import React, {memo} from 'react';
import styled from 'styled-components';
import {IComment} from '../../model/commentModel';
import {useTranslation} from 'react-i18next';
import CommentInput from './CommentInput';
import {IWorkerState} from '../../model/workerState';
import NonEditingCommentFooter from './NonEditingCommentFooter';
import {ILike} from '../../model/reactionModel';
import config from '../../service/config';
import Linkify from 'react-linkify';
import {AriaAlert} from '../util/StyledElements';

const CommentBodyWrapper = styled.div<{isReply: boolean; isEditing: boolean}>`
   margin-left: 2rem;
   margin-top: 0.75rem;
   font-weight: 400;
   word-break: break-word;
   white-space: pre-wrap;

   a {
      color: inherit;
      text-decoration: underline;
      border: 1px dashed transparent;
      outline: none;

      &:focus, &:hover {
         text-decoration: none;
         border-color: currentColor;
      }

      &:active {
         text-decoration: none;
         border-color: currentColor;
         border-style: solid;
      }
   }
`;

export const CommentBodyBase: React.FC<ICommentBodyProps> = ({comment, isReply, loadingState, isCommentOwner, commentRepliesLength, expandReplies, setExpandReplies, setDeleteAnnotationOnSave, saveEdit, handleExpandReplies, setShowDeleteAlertModal, deleteAnnotationOnSave}) => {
   const {t} = useTranslation();
   const userHasLikedComment = comment.likes && comment.likes.some((like: ILike) => like.UserId === config.user.TechSmithId);

   const getCommentText = () => {
      if (comment.text) {
         return comment.text;
      } else if (comment.annotation) {
         return t('conversations.annotationAttached');
      }
      return t('conversations.commentDeletedText');
   };

   return (
      <CommentBodyWrapper
         isReply={isReply}
         isEditing={comment.isEditing}
         data-test-id="conversation-comment-body"
      >
         {(!comment.isEditing || !isCommentOwner || comment.isResolved) && (
            <Linkify
               componentDecorator={(decoratedHref, decoratedText, key) => (
                  <a
                     target="blank"
                     href={decoratedHref}
                     key={key}
                     rel="noopener noreferrer nofollow"
                  >
                     {decoratedText}
                  </a>
               )}
            >
               {getCommentText()}
            </Linkify>
         )}
         {comment.isEditing && (
            <CommentInput
               inputId={`${comment.id}-input`}
               hasAnnotation={false}
               setDeleteAnnotationOnSave={setDeleteAnnotationOnSave}
               isCommentOwner={isCommentOwner}
               isReply={isReply}
               comment={comment}
               onSubmit={(editedText: string) => saveEdit(editedText)}
               loadingState={loadingState}
               setShowDeleteAlertModal={setShowDeleteAlertModal}
               deleteAnnotationOnSave={deleteAnnotationOnSave}
            />
         )}
         {!comment.isEditing && (
            <NonEditingCommentFooter
               userHasLikedComment={userHasLikedComment}
               comment={comment}
               isReply={isReply}
               handleExpandReplies={handleExpandReplies}
               commentRepliesLength={commentRepliesLength}
               expandReplies={expandReplies}
               setExpandReplies={setExpandReplies}
            />
         )}
         <AriaAlert
            role="alert"
            id={`comment-editing-alert-${comment.id}`}
         >
            {t('conversations.editCommentClickedAlert')}
         </AriaAlert>
      </CommentBodyWrapper>
   );
};

export interface ICommentBodyProps {
   comment: IComment;
   isReply: boolean;
   loadingState: IWorkerState;
   deleteAnnotationOnSave: boolean;
   isCommentOwner: boolean;
   setDeleteAnnotationOnSave(deleteAnnotationOnSave: boolean): void;
   handleExpandReplies(): void;
   saveEdit(editedText: string): void;
   setShowDeleteAlertModal(showDeleteAlertModal: boolean): void;
   expandReplies: boolean;
   commentRepliesLength: number;
   setExpandReplies(expandReplies: boolean): void;
}

export default memo(CommentBodyBase);
